import { useAuthStore, useOrganizationStore } from '@/stores/store';

/**
 * Check if current user has a specific organization role OR is a global_admin.
 * Global admins have access to everything regardless of organization roles.
 *
 * This hook combines the check for user-level global_admin role with organization
 * role checks, following the pattern established in RoleGuard.tsx.
 *
 * @param requiredRole - The organization role to check for (e.g., 'org_admin', 'org_member')
 * @returns true if user is global_admin OR has the required org role
 *
 * @example
 * // In a component that requires org_admin access:
 * const isOrgAdmin = useHasOrgRole('org_admin');
 * if (!isOrgAdmin) {
 *   return <AccessDenied />;
 * }
 */
export function useHasOrgRole(requiredRole: string): boolean {
  const { user } = useAuthStore();
  const { currentOrg } = useOrganizationStore();

  // Global admins have access to everything
  if (user?.role === 'global_admin') return true;

  // Check organization role
  return currentOrg?.roles?.some((r) => r === requiredRole) ?? false;
}
